import { ClickAwayListener } from '@mui/base';
import { Box } from '@mui/system';
import noop from 'lodash/noop';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { subscribeToReconnect } from '../../apollo/client';
import {
  mergeChatRoom,
  multiChatTotalUnreadSubscribeToMore,
} from '../../apollo/merge';
import {
  ChatQuery,
  MemberBasicsFragment,
  NewMessagesDocument,
  NewMessagesSubscription,
  NewMessagesSubscriptionVariables,
  SelfQuery,
  useChatLazyQuery,
  useMembersListTotalsQuery,
  useNewDeliveryErrorsSubscription,
  useSelfQuery,
} from '../../graphql/types';
import { TrackingEventData } from '../../hooks/useTracking';
import { BORDER_GRAY, WHITE } from '../../theme';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { BookSessionModal } from '../BookSessionModal';
import { MultiChatList } from '../ChatList/MultiChatList';
import { Dialog } from '../reusable/Dialog';
import { FloatingButton } from '../reusable/FloatingButton';
import { ChatSize, useChatContext } from './Context';
import { CreateBulkChat } from './CreateBulkChat';
import { CreateChat } from './CreateChat';
import { Header } from './Header';
import { Messages } from './Messages';
import { Toolbar } from './Toolbar';
import { getMemberHeaderContent } from './util';

const measuredChatHeaderHeight = 54;
const measuredChatToolbarHeight = 56;
const measuredChatBannerHeight = 30;

const StyledChatContainer = styled.div<{
  $open?: boolean;
  $showBanner?: boolean;
  $chatSize?: ChatSize;
  $adjustSpacingForChatList?: boolean;
}>`
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 100%;

  position: fixed;
  z-index: 2;

  right: ${({ $adjustSpacingForChatList }) =>
    $adjustSpacingForChatList ? '96px' : '24px'};
  bottom: 0;

  width: ${({ $chatSize }) =>
    $chatSize === ChatSize.Maximized ? '500px' : '352px'};

  ${({ $chatSize, $showBanner }) => {
    const measuredExtrasHeight =
      measuredChatHeaderHeight +
      measuredChatToolbarHeight +
      ($showBanner ? measuredChatBannerHeight : 0);
    switch ($chatSize) {
      case ChatSize.Open:
        return `
          height: calc(327px + ${measuredExtrasHeight}px);
        `;
      case ChatSize.Maximized:
        return `
          height: calc(600px + ${measuredExtrasHeight}px);
        `;
      case ChatSize.Minimized:
      default:
        return `
          height: ${measuredChatHeaderHeight}px;
        `;
    }
  }};

  max-height: calc(100vh - 60px);

  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  background: ${WHITE};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  border: 1px solid ${BORDER_GRAY};
  border-bottom: 0;

  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.25s ease-in-out, width 0.25s ease-in-out,
    height 0.25s ease-in-out;
`;

interface ChatViewProps extends ChatProps {
  chat?: ChatQuery['chatRoom'];
  self: SelfQuery['self'];
  loading?: boolean;
  error?: boolean;
  refetch?: () => void;
  fetchMore?: () => void;
  initialLoading?: boolean;
  paginationError?: boolean;
  hasNewMessages?: boolean;
  setHasNewMessages?: (hasNewMessages: boolean) => void;
}

export const ChatView: React.FC<ChatViewProps> = ({
  closeable = false,
  chat,
  self,
  loading,
  error,
  refetch,
  fetchMore,
  initialLoading,
  paginationError,
  hasNewMessages,
  setHasNewMessages,
}) => {
  const location = useLocation();
  const {
    chatSize,
    member,
    chatListOpen,
    setChatListOpen,
    chatId,
    member: passedInMember,
  } = useChatContext();

  const [bookSessionModalOpen, setBookSessionModalOpen] = useState(false);
  const [deliveryErrors, setDeliveryErrors] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  // GOTCHA: newDeliveryErrors is a standalone subscription not associated with any query,
  // There's no cache object to merge new results into, so we aggregate results in state.
  // Note that useSubscription automatically unsubscribes when the component unmounts.
  const { data: deliveryErrorsData } = useNewDeliveryErrorsSubscription();
  // biome-ignore lint/correctness/useExhaustiveDependencies: causes infinite loop
  useEffect(() => {
    if (deliveryErrorsData?.newDeliveryErrors.error) {
      setDeliveryErrors(
        `${deliveryErrors}\n\n ${deliveryErrorsData.newDeliveryErrors.error}`,
      );
      if (!dialogOpen) {
        setDialogOpen(true);
      }
    }
  }, [deliveryErrorsData]);
  const acknowledgeErrors = useMemo(
    () => () => {
      setDialogOpen(false);
      setTimeout(() => {
        // Allow dialog time to close before scrubbing messages for a smoother visual
        setDeliveryErrors('');
      }, 2000);
    },
    [],
  );
  const trackingCategory = EventCategories.Chat;
  const trackingSubCategory = EventSubCategories.ChatList;
  const mixpanelData: TrackingEventData = {
    event_category: trackingCategory,
    event_subcategory: trackingSubCategory,
    event_label: EventLabels.MessageNotSentAlert,
  };

  const {
    data: membersListTotals,
    subscribeToMore: subscribeToMoreMembersTotals,
  } = useMembersListTotalsQuery({
    variables: {},
  });

  const showBanner = member?.fitCoach?.id !== self?.id;
  useEffect(() => {
    if (subscribeToMoreMembersTotals) {
      const unsubscribeFromMore = multiChatTotalUnreadSubscribeToMore(
        subscribeToMoreMembersTotals,
      );
      return () => {
        unsubscribeFromMore();
      };
    } else {
      return noop;
    }
  }, [subscribeToMoreMembersTotals]);

  const hasUnreadMessages =
    (membersListTotals?.self?.coachData?.totalUnreadMessageCount || 0) > 0;

  const headerContent = getMemberHeaderContent({ member });

  const open = chatSize !== ChatSize.Closed;
  const scrollRef = useRef<HTMLDivElement>(null);

  const sessionRefetchQueries = () => {
    if (location.pathname.includes('app/sessions/')) {
      return ['coachSessions'];
    }
    if (location.pathname.includes(`app/members/${member?.id}`)) {
      return ['memberCoachSessions', 'memberCoachSessionsCount'];
    }
    return [];
  };

  const chatWindowType = useMemo(() => {
    return passedInMember ? (
      <CreateChat closeable />
    ) : (
      <CreateBulkChat closeable />
    );
  }, [passedInMember]);

  return (
    <>
      <StyledChatContainer
        $open={open}
        $chatSize={chatSize}
        $showBanner={showBanner}
        $adjustSpacingForChatList={closeable}
        data-testid="chat-container"
        data-itemid={chat?.id}
      >
        {chatId ? (
          <>
            <Header
              content={headerContent}
              closeable={closeable}
              showBanner={showBanner}
            />
            <Messages
              chat={chat}
              self={self}
              loadingMore={loading}
              error={error}
              refetch={refetch}
              fetchMore={fetchMore}
              initialLoading={initialLoading}
              paginationError={paginationError}
              scrollRef={scrollRef}
              hasNewMessages={hasNewMessages}
              setHasNewMessages={setHasNewMessages}
            />
            <Toolbar
              chat={chat}
              scrollContainerRef={scrollRef}
              onOpenBookSessionModal={() => setBookSessionModalOpen(true)}
            />
          </>
        ) : (
          chatWindowType
        )}
      </StyledChatContainer>

      {closeable && (
        <>
          <FloatingButton
            testId="FloatingChatButton"
            open={chatListOpen}
            onClick={() => setChatListOpen(!chatListOpen)}
            showUnreadIcon={hasUnreadMessages}
            trackingOpenEvent={EventNames.ChatIconClick}
            trackingCategory={trackingCategory}
            trackingSubCategory={trackingSubCategory}
          />
          {chatListOpen && (
            <ClickAwayListener onClickAway={() => setChatListOpen(false)}>
              <Box>
                <MultiChatList onClick={() => setChatListOpen(false)} />
              </Box>
            </ClickAwayListener>
          )}
        </>
      )}

      <BookSessionModal
        member={member as MemberBasicsFragment}
        closeModal={() => setBookSessionModalOpen(false)}
        isOpen={bookSessionModalOpen}
        refetchQueries={sessionRefetchQueries()}
        testId="chat-book-session-modal"
      />

      <Dialog
        open={dialogOpen}
        title="Some messages could not be sent"
        message={deliveryErrors}
        confirmLabel="Acknowledge"
        confirmAction={acknowledgeErrors}
        mixpanelData={mixpanelData}
      />
    </>
  );
};

interface ChatProps {
  closeable?: boolean;
}

export const Chat: React.FC<ChatProps> = ({ closeable }) => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [paginationError, setPaginationError] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  const { chatId } = useChatContext();
  const { data: selfData } = useSelfQuery({
    fetchPolicy: 'cache-first',
  });
  const [
    fetchChat,
    {
      data: chatData,
      loading: chatLoading,
      error: chatError,
      refetch: chatRefetch,
      fetchMore: chatFetchMore,
      subscribeToMore,
    },
  ] = useChatLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      setInitialLoading(false);
    },
  });

  useEffect(() => {
    if (chatId) {
      fetchChat({
        variables: {
          chatId,
          input: {
            first: 10,
          },
          skipSelf: true,
        },
      });
    }
  }, [chatId, fetchChat]);

  useEffect(() => {
    let unsubscribeFromNewMessages = noop;
    let unsubscribeFromReconnect = noop;
    if (chatId) {
      unsubscribeFromNewMessages = subscribeToMore<
        NewMessagesSubscription,
        NewMessagesSubscriptionVariables
      >({
        document: NewMessagesDocument,
        variables: { chatId },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) {
            return prev;
          }
          const newMessage = subscriptionData.data.newMessages.message;
          if (!newMessage || !prev.chatRoom) {
            return prev;
          }

          const newChatRoom = mergeChatRoom(
            prev.chatRoom,
            subscriptionData.data,
          );
          if (
            newChatRoom.messages.edges.length !==
              prev.chatRoom.messages.edges.length &&
            newChatRoom.messages.edges[newChatRoom.messages.edges.length - 1]
              .node.user.id !== selfData?.self?.id
          ) {
            setHasNewMessages(true);
          }

          return {
            ...prev,
            chatRoom: newChatRoom,
          };
        },
      });

      unsubscribeFromReconnect = subscribeToReconnect(chatRefetch);
    }

    return () => {
      unsubscribeFromNewMessages();
      unsubscribeFromReconnect();
    };
  }, [chatId, chatRefetch, selfData?.self?.id, subscribeToMore]);

  const chat = chatData?.chatRoom;

  const loadMoreChats = () => {
    setPaginationError(false);
    chatFetchMore({
      variables: {
        chatId,
        input: {
          first: 10,
          after: chat?.messages?.pageInfo?.endCursor,
        },
        skipSelf: true,
      },
      /**
       * Apollo doesn't route errors encountered through `fetchMore` to the
       * `error` object in the original hook call. This is the only way to
       * we could find to catch pagination errors.
       * https://github.com/apollographql/react-apollo/issues/2759#issuecomment-496495329
       */
    }).catch(() => setPaginationError(true));
  };

  return (
    <ChatView
      chat={chatId ? chat : null}
      loading={chatLoading}
      error={Boolean(chatError)}
      self={selfData?.self}
      closeable={closeable}
      refetch={chatRefetch}
      fetchMore={loadMoreChats}
      initialLoading={initialLoading}
      paginationError={paginationError}
      hasNewMessages={hasNewMessages}
      setHasNewMessages={setHasNewMessages}
    />
  );
};
